import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

interface CollapsibleSidebarProps {
  children: React.ReactNode;
  component?: string;
  initiallyOpen?: boolean;
  className?: string;
}

/**
 * A reusable collapsible sidebar component with consistent styling and behavior
 */
export function CollapsibleSidebar(props: CollapsibleSidebarProps): JSX.Element {
  const {children, component = 'CollapsibleSidebar', initiallyOpen = true, className} = props;
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  return (
    <div
      aria-expanded={isOpen}
      {...getTestProps({component, element: 'root'})}
      className={classNames(
        css(styles.sidebarWrapper),
        !isOpen && css(styles.closed),
        'hide-on-print',
        className
      )}
    >
      <div
        className={classNames(css(styles.tabsContainer), !isOpen && css(styles.tabsClosed))}
        {...getTestProps({component, element: 'tabsContainer'})}
      >
        <div className={classNames('scrollbar-override', css(styles.sidebar))}>{children}</div>
      </div>
      <Button
        buttonStyle={ButtonStyle.CIRCLE_ICON}
        className={css(styles.opener)}
        icon={isOpen ? 'chevron-left' : 'chevron-right'}
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        size={ButtonSize.MEDIUM}
      />
    </div>
  );
}

const SIDEBAR_WIDTH = 240;
const SIDEBAR_PADDING = 16;

const styles = StyleSheet.create({
  pane: {
    height: 'calc(100% - 7px)',
    marginTop: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    opacity: 1,
    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in',
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    transform: 'translate(0px, 0px)',
    height: '100%',
  },
  tabsClosed: {
    opacity: 0,
    transform: `translate(-${SIDEBAR_WIDTH}px, 0px)`,
    width: SIDEBAR_WIDTH - SIDEBAR_PADDING,
    pointerEvents: 'none',
  },
  sidebarWrapper: {
    overflow: 'hidden',
    position: 'relative',
    width: SIDEBAR_WIDTH,
    transition: '0.3s ease-in-out',
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '20px',
    gap: 10,
    width: SIDEBAR_WIDTH,
    minWidth: SIDEBAR_WIDTH,
    paddingRight: SIDEBAR_PADDING,
    overflowY: 'auto',
    overflowX: 'hidden',
    height: '100%',
  },
  closed: {
    width: 20,
  },
  opener: {
    position: 'absolute',
    top: -10,
    right: 0,
  },
});
